import React, { useState, useEffect } from "react";

import './Pagination.css';
import backIcon from '../../assets/images/back.svg';
import nextIcon from '../../assets/images/next.svg';

/**
 * Dynamic pagination component
 * 
 * @param {*} totalPages - Total number of pages
 * @param {*} setOffset - Function to set the offset
 * @param {*} limit - Number of items per page
 * @param {*} currentPage - Current page
 * @returns - Pagination component
 */
const Pagination = ({ totalPages, setOffset, limit, currentPage }) => {
  const [internalPage, setInternalPage] = useState(currentPage);

  // Sync internal page state with currentPage from parent
  useEffect(() => {
    setInternalPage(currentPage);
  }, [currentPage]);

  // Update offset when internalPage changes
  useEffect(() => {
    const newOffset = (internalPage - 1) * limit;
    setOffset(newOffset);
  }, [internalPage, limit, setOffset]);

  // Generate the pages to display in the pagination
  const getPagesToDisplay = () => {
    const pages = [];

    if (totalPages <= 10) {
      // Show all pages if totalPages <= 10
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show the first page
      pages.push(1);

      if (internalPage > 4) {
        pages.push('...');
      }

      // Calculate the range to show around the current page
      const start = Math.max(2, internalPage - 2);
      const end = Math.min(totalPages - 1, internalPage + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (internalPage < totalPages - 3) {
        pages.push('...');
      }

    }

    return pages;
  };

  const handleNextClick = () => {
    setInternalPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handleBackClick = () => {
    setInternalPage((prev) => Math.max(prev - 1, 1));
  };

  const handlePageClick = (page) => {
    if (page !== '...') setInternalPage(page);
  };

  return (
    <div className="pagination">
      <button
        onClick={handleBackClick}
        disabled={internalPage === 1}
        className="pagination-button primary-button"
      >
        <img src={backIcon} alt="Previous Page Icon" />
      </button>

      {getPagesToDisplay().map((page, i) => (
        <button
          key={i}
          onClick={() => handlePageClick(page)}
          disabled={internalPage === page || page === '...'}
          className={internalPage === page ? 'current-page pagination-button primary-button' : 'pagination-button primary-button'}
        >
          {page}
        </button>
      ))}

      <button
        onClick={handleNextClick}
        disabled={internalPage === totalPages || totalPages === 0}
        className="pagination-button primary-button"
      >
        <img src={nextIcon} alt="Next Page Icon" />
      </button>
    </div>
  );
};

export default Pagination;



